/**
 * ─────────────────────────────────────────────
 * src/services/sprintService.js
 * ─────────────────────────────────────────────
 * Sprints on a project's Timeline tab.
 *
 *   GET    /projects/:projectId/sprints             the sprint table
 *   GET    /projects/:projectId/sprints/:sprintId   one sprint
 *   POST   /projects/:projectId/sprints             create
 *   PUT    /projects/:projectId/sprints/:sprintId   edit
 *   DELETE /projects/:projectId/sprints/:sprintId   remove
 *
 * Paths are written WITHOUT the "/api" prefix — the shared axios instance
 * supplies it via baseURL.
 */
import api from "../lib/api";

const getSprints = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/sprints`);
  return response.data;
};

const getSprintById = async (projectId, sprintId) => {
  const response = await api.get(`/projects/${projectId}/sprints/${sprintId}`);
  return response.data;
};

// The project's Kanban lists, for the "Lists under this sprint" dropdown.
// Each carries `assignedTo` — the sprint already covering it, or null when it
// is free. Pass the sprint being edited so its own lists are not reported as
// taken by itself.
const getAvailableLists = async (projectId, sprintId) => {
  const response = await api.get(`/projects/${projectId}/sprints/lists`, {
    params: sprintId ? { sprintId } : undefined,
  });
  return response.data;
};

// Payload: { name, scope, duration, startDate, endDate, status, lists[] }
// Dates are plain "YYYY-MM-DD" strings, exactly as the form holds them.
const createSprint = async (projectId, payload) => {
  const response = await api.post(`/projects/${projectId}/sprints`, payload);
  return response.data;
};

const updateSprint = async (projectId, sprintId, payload) => {
  const response = await api.put(
    `/projects/${projectId}/sprints/${sprintId}`,
    payload,
  );
  return response.data;
};

// ─────────────────────────────────────────────
// READ A SPRINT PLAN OUT OF A FILE
//
// Returns the rows found, saving nothing — the user reviews them and then
// calls importSprints. The file is read on the server and discarded.
//
// Longer timeout than the instance default: the model takes a while on a
// multi-page plan, and a timeout here would look like a broken feature.
// ─────────────────────────────────────────────
const parseSprintFile = async (projectId, file) => {
  const form = new FormData();
  form.append("file", file);
  const response = await api.post(
    `/projects/${projectId}/sprints/parse`,
    form,
    { timeout: 130000, headers: { "Content-Type": undefined } },
  );
  return response.data;
};

// Same, for a file picked from Google Drive. The backend downloads it with
// the short-lived token the picker returned; the browser never handles the
// file's bytes.
const parseSprintDriveFile = async (projectId, { fileId, token }) => {
  const response = await api.post(
    `/projects/${projectId}/sprints/parse`,
    { driveFileId: fileId, driveToken: token },
    { timeout: 130000 },
  );
  return response.data;
};

// Creates the sprints the user confirmed from the preview.
const importSprints = async (projectId, sprints) => {
  const response = await api.post(`/projects/${projectId}/sprints/import`, {
    sprints,
  });
  return response.data;
};

const deleteSprint = async (projectId, sprintId) => {
  const response = await api.delete(
    `/projects/${projectId}/sprints/${sprintId}`,
  );
  return response.data;
};

const sprintService = {
  getSprints,
  getSprintById,
  getAvailableLists,
  createSprint,
  parseSprintFile,
  parseSprintDriveFile,
  importSprints,
  updateSprint,
  deleteSprint,
};

export default sprintService;
